import { hora12, instanteLocal, type AgendaDia, type CitaVista } from '@barbones/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { api, esEncolada } from '@/api/cliente';
import { avisarError } from '@/lib/avisos';

interface Movimiento {
  cita: CitaVista;
  inicio: string;
  barberoId: string | null;
  /** `undefined`: se conserva el preferido (al soltar en «Sin asignar»). */
  barberoPreferidoId?: string | null;
  /** Es el «Deshacer» de un movimiento anterior. */
  deshacer?: boolean;
}

/**
 * Guarda una cita arrastrada: la mueve al instante en la agenda del día, la regresa si el servidor
 * rechaza, ofrece «Deshacer» y refresca la agenda y «Por asignar».
 */
export function useMoverCita(dia: string, barberos: AgendaDia['barberos']) {
  const qc = useQueryClient();
  const clave = ['agenda', 'dia', dia];

  const mutacion = useMutation({
    mutationFn: ({ cita, inicio, barberoId, barberoPreferidoId }: Movimiento) =>
      api<CitaVista>(`/citas/${cita.id}/reprogramar`, {
        method: 'PATCH',
        body: { inicio, barberoId, ...(barberoPreferidoId !== undefined && { barberoPreferidoId }) },
      }),
    onMutate: async (m) => {
      // Un refresco en tiempo real a medio camino no debe pisar el movimiento optimista.
      await Promise.all([qc.cancelQueries({ queryKey: ['agenda'] }), qc.cancelQueries({ queryKey: ['pendientes'] })]);
      const antes = qc.getQueryData<AgendaDia>(clave);
      const duracion = new Date(m.cita.fin).getTime() - new Date(m.cita.inicio).getTime();
      const movida: CitaVista = {
        ...m.cita,
        inicio: m.inicio,
        fin: new Date(new Date(m.inicio).getTime() + duracion).toISOString(),
        barberoId: m.barberoId,
        barbero: barberos.find((b) => b.id === m.barberoId)?.nombre ?? null,
        ...(m.barberoPreferidoId !== undefined && { barberoPreferidoId: m.barberoPreferidoId }),
      };
      if (antes) qc.setQueryData<AgendaDia>(clave, { ...antes, citas: antes.citas.map((c) => (c.id === m.cita.id ? movida : c)) });
      return { antes };
    },
    onError: (error, _m, contexto) => {
      // Encolada sin conexión: se enviará al reconectar, así que el movimiento se queda.
      if (!esEncolada(error) && contexto?.antes) qc.setQueryData(clave, contexto.antes);
      // Choque con otra cita: el servidor sugiere horarios libres cercanos.
      const alternativas = (error as { details?: { alternativas?: { inicio: string }[] } }).details?.alternativas;
      if (!esEncolada(error) && alternativas?.length) {
        toast.error(error.message, { description: `Libres: ${alternativas.map((a) => hora12(new Date(a.inicio))).join(', ')}` });
        return;
      }
      avisarError(error);
    },
    onSuccess: (_r, m) => {
      if (m.deshacer) {
        toast.success('Movimiento deshecho');
        return;
      }
      const destino = barberos.find((b) => b.id === m.barberoId)?.nombre ?? 'Sin asignar';
      const original = m.cita;
      toast.success(`Cita movida a ${hora12(new Date(m.inicio))} con ${destino}`, {
        duration: 6000,
        action: {
          label: 'Deshacer',
          onClick: () => {
            const actual = qc.getQueryData<AgendaDia>(clave)?.citas.find((c) => c.id === original.id) ?? original;
            mutacion.mutate({ cita: actual, inicio: original.inicio, barberoId: original.barberoId, barberoPreferidoId: original.barberoPreferidoId, deshacer: true });
          },
        },
      });
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: ['agenda'] });
      void qc.invalidateQueries({ queryKey: ['pendientes'] });
    },
  });

  return {
    mover: (cita: CitaVista, destino: { barberoId: string | null; hora: string }) =>
      mutacion.mutate({
        cita,
        inicio: instanteLocal(dia, destino.hora).toISOString(),
        barberoId: destino.barberoId,
        ...(destino.barberoId !== null && { barberoPreferidoId: destino.barberoId }),
      }),
    moviendo: mutacion.isPending,
  };
}
